import React, { PureComponent } from 'react';
import { Modal, Button } from 'semantic-ui-react';
import Dropzone from 'react-dropzone';
import ResizeSensor from 'resize-sensor';
import CameraImage from './CameraImage';
import { resizeImage, uploadImage, rescaleImageInModal } from '../../../helpers/ImageUtility';
import Loader from '../../../components/loader';
import { CloseDark } from '../../../icons/icons';
import '../../styles/ImageModal.scss';
import { ValueChangedForFieldFunc } from '../../formeditor-types';

export interface ImageModalProps {
  onClose: AnyFunc;
  valueUpdated?: ValueChangedForFieldFunc;
  getSharedFormImageUrl?: AnyFunc;
  promptDeleteExisting?: boolean;
  imageQuality?: string;
  retainImageAsAttachment?: string;
  width?: number;
  height?: number;
  dateStamp?: boolean;
  fontSize?: number;
  fontName?: string;
  foregroundColor?: TypeMePlease;
  color: TypeMePlease;
  formId?: string;
  fieldName?: string;
  fieldId?: string;
  rowNumber?: number;
  isSketchBackground?: boolean;
  fileUrls?: TypeMePlease;
  w?: number;
  h?: number;
}

export default class ImageModal extends PureComponent<ImageModalProps, TypeMePlease> {
  private maxFileSizeInMegabytes: number;
  private maxFileSizeInBytes: number;
  private resizeSensor: TypeMePlease;
  private dropzoneRef: TypeMePlease;
  private cameraImage: TypeMePlease;
  private previewImgRef: TypeMePlease;

  constructor(props) {
    super(props);

    this.openFilePicker = this.openFilePicker.bind(this);
    this.fileAccepted = this.fileAccepted.bind(this);
    this.fileRejected = this.fileRejected.bind(this);
    this.handleUploadImageClicked = this.handleUploadImageClicked.bind(this);
    this.handleRemoveImageClicked = this.handleRemoveImageClicked.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleTakePicture = this.handleTakePicture.bind(this);
    this.toggleDropZoneView = this.toggleDropZoneView.bind(this);
    this.toggleCameraView = this.toggleCameraView.bind(this);
    this.handleShowPreview = this.handleShowPreview.bind(this);
    this.handleUseImage = this.handleUseImage.bind(this);
    this.handleSwitchCamera = this.handleSwitchCamera.bind(this);
    this.handlePreviewImageLoad = this.handlePreviewImageLoad.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);

    this.state = {
      uploading: false,
      uploadError: null,
      replaceImage: false,
      currentView: 'dropzone',
      previewUri: null,
      originalUri: null,
      originalFile: null,
      switchCameras: null,
      previewDimensions: null,
      previewScaledDimensions: null
    };

    this.maxFileSizeInMegabytes = 20;
    this.maxFileSizeInBytes = this.maxFileSizeInMegabytes * 1024 * 1024;
  }

  componentDidMount() {
    const d = document;
    const body = d.getElementsByTagName('body')[0];

    this.resizeSensor = new ResizeSensor(body, this.updateDimensions);
  }

  componentWillUnmount() {
    if (this.resizeSensor) {
      this.resizeSensor.detach();
    }
  }

  openFilePicker() {
    this.dropzoneRef.open();
  }

  handleUploadImageClicked() {
    this.setState({ replaceImage: true });
  }

  handleRemoveImageClicked() {
    this.props.valueUpdated({ value: '' });
  }

  handleModalClose() {
    this.props.onClose();
  }

  async fileAccepted(files) {
    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name;
      const fileExtension = fileName.match(/\.([^.]+)$/)[1];
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].indexOf(fileExtension.toLowerCase()) > -1) {
        if (!this.state.templateName) {
          const templateName = fileName.substring(0, fileName.length - fileExtension.length - 1);
          this.setState({ templateName });
        }

        this.setState({ uploading: true, uploadError: null });

        const dateStampOptions = {
          dateStamp: this.props.dateStamp,
          fontSize: this.props.fontSize,
          fontName: this.props.fontName,
          foregroundColor: this.props.color
        };
        const resizedImage = await resizeImage(file, this.props.imageQuality, this.props.width, this.props.height, dateStampOptions);
        this.setState({ currentView: 'preview', previewUri: resizedImage, originalFile: file, uploading: false });
      } else {
        this.setState({
          uploadError: `${fileExtension.toUpperCase()} is an invalid file type. Please select a JPG, JPEG, PNG, GIF, or BMP file.`
        });
      }
    }
  }

  fileRejected(files) {
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size && file.size > this.maxFileSizeInBytes) {
        const fileSize = Math.round((file.size / 1024 / 1024) * 100) / 100;
        this.setState({ uploadError: `The file you selected is ${fileSize}MB, which is larger than ${this.maxFileSizeInMegabytes}MB` });
      } else {
        const fileExtension = file.name.match(/\.([^.]+)$/)[1];
        this.setState({
          uploadError: `${fileExtension.toUpperCase()} is an invalid file type. Please select a JPG, PNG, GIF, or BMP file.`
        });
      }
    }
  }

  async handleShowPreview(dataUri) {
    this.setState({ uploading: true });
    const dateStampOptions = {
      dateStamp: this.props.dateStamp,
      fontSize: this.props.fontSize,
      fontName: this.props.fontName,
      foregroundColor: this.props.color
    };
    const resizedImage = await resizeImage(dataUri, this.props.imageQuality, this.props.width, this.props.height, dateStampOptions);
    this.setState({ currentView: 'preview', previewUri: resizedImage, originalUri: dataUri, uploading: false });
  }

  async handleUseImage() {
    this.setState({ uploading: true });

    let fieldId = this.props.fieldId;
    if (this.props.rowNumber !== undefined) {
      fieldId += `_${this.props.rowNumber}`;
    }
    if (this.props.isSketchBackground) {
      fieldId += '_FileId';
    }
    const { fileId, attachmentId } = await uploadImage(
      this.state.originalUri ? this.state.originalUri : this.state.originalFile,
      this.state.previewUri,
      this.props.retainImageAsAttachment,
      this.props.formId,
      this.props.fieldName,
      fieldId
    );

    if (this.props.fileUrls) {
      await this.props.getSharedFormImageUrl(fileId);
    }

    this.setState({ uploading: false });
    const jsonValue = { FileId: fileId, AttachmentId: attachmentId };
    this.props.valueUpdated({ value: JSON.stringify(jsonValue) });
    this.props.onClose();
  }

  toggleDropZoneView() {
    this.setState({ currentView: 'dropzone', originalUri: null, originalFile: null });
  }

  async toggleCameraView() {
    if (await CameraImage.hasCameras()) {
      this.setState({ currentView: 'camera', originalUri: null, originalFile: null });
      if (await CameraImage.hasMultipleCameras()) {
        this.setState({ switchCameras: true });
      }
    } else {
      this.setState({ currentView: 'nocamera', originalUri: null, originalFile: null });
    }
  }

  async handleTakePicture() {
    this.setState({ uploading: true });
    await this.cameraImage.handleTakePicture();
    this.setState({ uploading: false });
  }

  handleSwitchCamera() {
    this.cameraImage.switchCameras();
  }

  updateDimensions() {
    if (this.cameraImage) {
      this.cameraImage.updateDimensions();
    }

    if (!this.state.previewDimensions || !this.previewImgRef) {
      return;
    }

    const { resizedWidth, resizedHeight } = rescaleImageInModal(
      this.state.previewDimensions.w,
      this.state.previewDimensions.h,
      this.previewImgRef.parentNode.parentNode.offsetWidth
    );

    this.setState({ previewScaledDimensions: { width: resizedWidth, height: resizedHeight } });
  }

  handlePreviewImageLoad() {
    this.setState({ previewDimensions: { w: this.previewImgRef.naturalWidth, h: this.previewImgRef.naturalHeight } }, () => {
      this.updateDimensions();
    });
  }

  render() {
    let dropzoneControl = null;
    let cameraControl = null;
    let actionControl = null;
    let previewControl = null;
    let noCameraControl = null;
    let dropzoneSelectedCss = 'btn-select-source';
    let dropzoneSelectedIconCss = '';
    let cameraSelectedCss = 'btn-select-source';
    let cameraSelectedIconCss = '';
    let actionCss = null;
    if (this.state.currentView === 'dropzone') {
      dropzoneControl = (
        <Dropzone
          name="file-upload"
          disableClick
          maxSize={this.maxFileSizeInBytes}
          multiple={false}
          disabled={this.state.uploading}
          style={{}}
          className="dropzone"
          activeClassName="active"
          rejectClassName="reject"
          disabledClassName="disabled"
          accept=".jpg, .jpeg, .png, .gif, .bmp, image/jpeg, image/gif, image/png, image/bmp"
          ref={(r) => {
            this.dropzoneRef = r;
          }}
          onDropAccepted={this.fileAccepted}
          onDropRejected={this.fileRejected}
        >
          <div className="upload-title">
            <i className="icon cloud upload upload-icon" />
            <div>
              <span>
                <span className="drag-and-drop-text">Drag and drop</span> a JPG, PNG, GIF, or BMP:
              </span>
            </div>
          </div>
          <div className="or-separator">
            <div className="line1" />
            <div className="line2" />
            <span>OR</span>
          </div>
          <div className="upload-buttons">
            <Button className="select-file secondary" onClick={this.openFilePicker} content="Browse" loading={this.state.uploading} />
          </div>
          <div className="upload-errors">{this.state.uploadError}</div>
        </Dropzone>
      );
      dropzoneSelectedCss += ' selected';
      dropzoneSelectedIconCss = ' selected';
    } else if (this.state.currentView === 'camera') {
      cameraControl = (
        <div className="camera">
          <CameraImage
            ref={(r) => {
              this.cameraImage = r;
            }}
            imageQuality={this.props.imageQuality}
            retainImageAsAttachment={this.props.retainImageAsAttachment}
            width={this.props.width}
            height={this.props.height}
            onSaveCanvas={this.handleShowPreview}
            onClose={this.handleModalClose}
          />
          <Loader isLoading={this.state.uploading} />
        </div>
      );

      let switchCameraButton = null;
      if (this.state.switchCameras) {
        switchCameraButton = <Button className="btn-switch-camera" onClick={this.handleSwitchCamera} />;
      }

      actionControl = (
        <div>
          <Button className="btn-camera-shutter" onClick={this.handleTakePicture}>
            <i className="camera big icon" />
          </Button>
          {switchCameraButton}
        </div>
      );

      actionCss = 'camera-mode';

      cameraSelectedCss += ' selected';
      cameraSelectedIconCss = ' selected';
    } else if (this.state.currentView === 'preview') {
      const previewHeight = this.state.previewScaledDimensions ? this.state.previewScaledDimensions.height : 0;
      const previewWidth = this.state.previewScaledDimensions ? this.state.previewScaledDimensions.width : 0;
      previewControl = (
        <div className="center-img">
          <img
            ref={(r) => {
              this.previewImgRef = r;
            }}
            src={this.state.previewUri}
            alt="preview"
            height={previewHeight}
            width={previewWidth}
            onLoad={this.handlePreviewImageLoad}
          />
          <Loader isLoading={this.state.uploading} text="Uploading..." />
        </div>
      );
      let backButton = (
        <Button className="cancel" onClick={this.toggleCameraView}>
          Take a new photo
        </Button>
      );

      if (this.state.originalFile) {
        backButton = (
          <Button className="cancel" onClick={this.toggleDropZoneView}>
            Choose another image
          </Button>
        );
      }

      actionControl = (
        <React.Fragment>
          {backButton}
          <Button className="done" onClick={this.handleUseImage}>
            Use This Image
          </Button>
        </React.Fragment>
      );

      if (this.state.originalFile) {
        dropzoneSelectedCss += ' selected';
        dropzoneSelectedIconCss = ' selected';
      } else {
        cameraSelectedCss += ' selected';
        cameraSelectedIconCss = ' selected';
      }
    } else if (this.state.currentView === 'nocamera') {
      cameraSelectedCss += ' selected';
      cameraSelectedIconCss = ' selected';
      noCameraControl = <div className="center-img">No Cameras Found</div>;
    }

    const uploadPromptModal = (
      <Modal open className="upload-image-modal" dimmer="inverted" size="tiny">
        <Modal.Header>
          {this.props.fieldName}
          <button className="close-button plain-button" tabIndex={-1} onClick={this.handleModalClose}>
            <CloseDark />
          </button>
        </Modal.Header>
        <Modal.Content>
          <Button className={`${dropzoneSelectedCss}`} onClick={this.toggleDropZoneView}>
            <i className={`image large icon${dropzoneSelectedIconCss}`} />
          </Button>
          <Button className={`${cameraSelectedCss}`} onClick={this.toggleCameraView}>
            <i className={`camera large icon${cameraSelectedIconCss}`} />
          </Button>
          <div className="line-break" />
          {dropzoneControl}
          {cameraControl}
          {previewControl}
          {noCameraControl}
        </Modal.Content>
        <Modal.Actions className={actionCss}>{actionControl}</Modal.Actions>
      </Modal>
    );

    //If existing image, then prompt
    if (this.props.promptDeleteExisting && !this.state.replaceImage) {
      const chooseActionModal = (
        <Modal open dimmer="inverted" size="tiny" onClose={this.handleModalClose} className="choose-action-modal" closeIcon>
          <Modal.Header>Choose Image</Modal.Header>
          <Modal.Content>
            <Button onClick={this.handleRemoveImageClicked} className="btn-prompt">
              <i className="image icon" />
              Clear Photo
            </Button>
            <Button onClick={this.handleUploadImageClicked} className="btn-prompt">
              <i className="camera icon" />
              Add new image
            </Button>
          </Modal.Content>
        </Modal>
      );
      return chooseActionModal;
    }

    return uploadPromptModal;
  }
}
